import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { AnimatePresence, motion } from "framer-motion";
import { CAROUSEL_PHOTOS } from "./constants";

export const LineageCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const lightboxOpen = lightbox !== null;
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const lightboxPrev = useCallback(
    () => setLightbox((i) => (i - 1 + CAROUSEL_PHOTOS.length) % CAROUSEL_PHOTOS.length),
    []
  );
  const lightboxNext = useCallback(
    () => setLightbox((i) => (i + 1) % CAROUSEL_PHOTOS.length),
    []
  );

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") lightboxPrev();
      if (e.key === "ArrowRight") lightboxNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxOpen, closeLightbox, lightboxPrev, lightboxNext]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || paused) return;
    const id = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => clearInterval(id);
  }, [emblaApi, paused]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );
  const scrollTo = useCallback(
    (i) => emblaApi && emblaApi.scrollTo(i),
    [emblaApi]
  );

  return (
    <div
      className="mt-20 sm:mt-24"
      data-testid="lineage-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
    >
      <div className="relative">
        <div
          className="overflow-hidden border border-[#C9A84C]/40"
          ref={emblaRef}
        >
          <div className="flex">
            {CAROUSEL_PHOTOS.map((photo, i) => (
              <div key={i} className="relative flex-[0_0_100%] min-w-0">
                <div className="aspect-[16/9] bg-[#E4DBCB] flex items-center justify-center overflow-hidden">
                  {photo.src ? (
                    <img
                      src={photo.src}
                      alt={photo.caption || `Photo ${i + 1}`}
                      loading="lazy"
                      onClick={() => setLightbox(i)}
                      data-testid={`carousel-image-${i}`}
                      className="w-full h-full object-cover cursor-zoom-in transition-transform duration-700 hover:scale-[1.03]"
                    />
                  ) : (
                    <span className="text-[#1A2535]/30 font-sans text-[0.6rem] tracking-[0.28em] uppercase">
                      Photo {i + 1} placeholder
                    </span>
                  )}
                </div>
                <p
                  className="mt-4 text-center font-sans text-[0.65rem] sm:text-xs tracking-[0.24em] uppercase text-[#C9A84C]"
                  data-testid={`carousel-caption-${i}`}
                >
                  {photo.caption || `\u00A0`}
                </p>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={scrollPrev}
          aria-label="Previous slide"
          data-testid="carousel-prev"
          className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-[#C9A84C]/60 bg-[#F0EAE0]/70 backdrop-blur-sm text-[#C9A84C] text-2xl leading-none pb-1 hover:bg-[#C9A84C]/10 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/60"
        >
          &#8249;
        </button>
        <button
          type="button"
          onClick={scrollNext}
          aria-label="Next slide"
          data-testid="carousel-next"
          className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-[#C9A84C]/60 bg-[#F0EAE0]/70 backdrop-blur-sm text-[#C9A84C] text-2xl leading-none pb-1 hover:bg-[#C9A84C]/10 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/60"
        >
          &#8250;
        </button>
      </div>

      <div className="flex justify-center gap-2.5 mt-7">
        {snaps.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`carousel-dot-${i}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              selected === i ? "bg-[#C9A84C] w-6" : "bg-[#1A2535]/25 w-2"
            }`}
          />
        ))}
      </div>

      {/* click-to-zoom lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0D1B2A]/95 backdrop-blur-sm px-4"
            onClick={closeLightbox}
            data-testid="carousel-lightbox"
          >
            <button
              type="button"
              onClick={closeLightbox}
              aria-label="Close"
              data-testid="lightbox-close"
              className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center rounded-full border border-[#C9A84C]/60 text-[#C9A84C] text-2xl leading-none hover:bg-[#C9A84C]/10 transition-colors duration-300"
            >
              &times;
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                lightboxPrev();
              }}
              aria-label="Previous image"
              data-testid="lightbox-prev"
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border border-[#C9A84C]/60 text-[#C9A84C] text-3xl leading-none pb-1 hover:bg-[#C9A84C]/10 transition-colors duration-300"
            >
              &#8249;
            </button>
            <motion.div
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={CAROUSEL_PHOTOS[lightbox].src}
                alt={CAROUSEL_PHOTOS[lightbox].caption}
                className="w-full max-h-[80vh] object-contain border border-[#C9A84C]/40"
              />
              <p className="mt-5 text-center font-sans text-xs tracking-[0.24em] uppercase text-[#C9A84C]">
                {CAROUSEL_PHOTOS[lightbox].caption}
              </p>
            </motion.div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                lightboxNext();
              }}
              aria-label="Next image"
              data-testid="lightbox-next"
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border border-[#C9A84C]/60 text-[#C9A84C] text-3xl leading-none pb-1 hover:bg-[#C9A84C]/10 transition-colors duration-300"
            >
              &#8250;
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
